import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { TextLoop } from "@/components/ui/text-loop";

const HERO_MESSAGES = [
  "Let's bring your OC to life",
  "Reference sheets, badges, and more",
  "Custom art for streamers and VTubers",
  "See your character the way you imagine it",
  "Full ref sheets, turnarounds, and expressions",
  "Album art, book covers, branding assets",
  "Emotes, avatars, and channel art",
];

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

/** One headline line that rises out of a mask on page load. */
function RevealLine({ children, delay, className }: { children: React.ReactNode; delay: number; className?: string }) {
  return (
    <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
      <motion.span
        className={`block ${className ?? ""}`}
        initial={{ y: "105%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: EASE_OUT_EXPO, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function HomeHero() {
  const heroRef = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();

  // Scroll-linked exit: the banner drifts slower than the page while the copy
  // lifts and fades, so leaving the hero feels layered rather than flat.
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const fadeUp = (delay: number) => ({
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, ease: EASE_OUT_EXPO, delay },
  });

  return (
    <section ref={heroRef} className="relative min-h-[70vh] flex items-center overflow-hidden">
      {/* Background Banner */}
      <motion.div className="absolute inset-0" style={reduce ? undefined : { y: bgY, scale: bgScale }}>
        <motion.img
          src="/static/images/decorations/banners/KomikoBanner.webp"
          alt="StudioComm Banner"
          className="h-full w-full object-cover"
          fetchPriority="high"
          decoding="async"
          initial={{ opacity: 0, scale: 1.12 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ opacity: { duration: 0.8 }, scale: { duration: 2.2, ease: EASE_OUT_EXPO } }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-bg)]/80 via-[var(--color-bg)]/50 to-[var(--color-bg)]" />
      </motion.div>

      {/* Hero Content - Left aligned */}
      <motion.div
        className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16"
        style={reduce ? undefined : { y: contentY, opacity: contentOpacity }}
      >
        <div className="max-w-xl">
          <h1 className="font-[var(--font-display)] text-4xl sm:text-5xl md:text-6xl text-[var(--color-text)] mb-6 drop-shadow-lg">
            <RevealLine delay={0.15}>Drawn By Hand,</RevealLine>
            <RevealLine delay={0.27} className="text-[var(--color-primary)] italic">
              Custom-Made For You
            </RevealLine>
          </h1>

          {/* TextLoop with rotating messages */}
          <motion.div
            className="text-lg sm:text-xl text-[var(--color-text)] mb-8 drop-shadow-md min-h-[2em]"
            {...fadeUp(0.55)}
          >
            <TextLoop
              interval={3}
              className="text-[var(--color-primary)] font-medium"
              transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
              variants={{
                initial: { y: 18, opacity: 0, filter: "blur(6px)" },
                animate: { y: 0, opacity: 1, filter: "blur(0px)" },
                exit: { y: -18, opacity: 0, filter: "blur(6px)" },
              }}
            >
              {HERO_MESSAGES.map((msg, idx) => (
                <span key={idx}>{msg}</span>
              ))}
            </TextLoop>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row gap-4" {...fadeUp(0.68)}>
            <a
              href="#artists"
              className="pressable cta-glow focus-ring inline-flex items-center justify-center h-12 px-8 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] font-semibold hover:bg-[var(--color-primary-light)] shadow-lg"
            >
              View Artists
            </a>
            <Link
              to="/how-it-works"
              className="pressable focus-ring inline-flex items-center justify-center h-12 px-8 rounded-full bg-[var(--color-bg)]/60 backdrop-blur-md border border-white/15 text-[var(--color-text)] font-medium hover:bg-white/10 hover:border-white/30"
            >
              How It Works
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator — a slow breathing drift instead of a bounce */}
      <motion.a
        href="#services"
        className="focus-ring absolute bottom-8 left-1/2 -translate-x-1/2 rounded-full p-2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        aria-label="Scroll down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
      >
        <motion.svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={reduce ? undefined : { opacity: contentOpacity }}
          animate={reduce ? undefined : { y: [0, 6, 0] }}
          transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
        >
          <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
        </motion.svg>
      </motion.a>
    </section>
  );
}
